import { motion } from "framer-motion";
import { MinusIcon, PlusIcon } from "lucide-react";
import type * as React from "react";
import { useEffect } from "react";
import { useFormContext } from "react-hook-form";
import type { ZodNumber } from "zod";

interface FormRangeProps {
	formKey: string;
	schema: ZodNumber;
	minVal?: number;
	maxVal?: number;
}

const FormNumber: React.FC<FormRangeProps> = ({
	formKey,
	schema,
	minVal,
	maxVal,
}) => {
	const { register, setValue, watch } = useFormContext();

	const min = schema.minValue ?? minVal ?? 0;
	const max = schema.maxValue ?? maxVal ?? 100;

	const step = (schema.meta()?.step as number) ?? 1;

	const isAngle = schema.meta()?.isAngle ?? false;

	const isRange = schema.meta()?.isRange ?? false;

	const value = watch(formKey) ?? 0;
	const defaultValue = schema.meta()?.defaultValue as number | undefined;

	useEffect(() => {
		if (watch(formKey)) return;

		if (defaultValue) {
			setTimeout(() => {
				setValue(formKey, defaultValue);
			}, 0);
		} else {
			setValue(formKey, min);
		}
	}, [defaultValue, formKey, min, setValue, watch]);

	return (
		<div className="flex max-w-64 items-center gap-3">
			{isRange && (
				<motion.button
					whileHover={{
						scale: 1.05,
					}}
					whileTap={{
						scale: 0.95,
					}}
					disabled={value <= min}
					onClick={() => {
						if (Number.isNaN(value))
							setValue(formKey, min, { shouldDirty: true });

						const newValue = value - step;
						if (newValue < min) return;
						setValue(formKey, newValue, { shouldDirty: true });
					}}
					className="rounded-full bg-slate-100 p-1 hover:bg-slate-200 disabled:pointer-events-none disabled:opacity-50 dark:bg-slate-700 dark:hover:bg-slate-600"
					type="button"
				>
					<MinusIcon size={20} />
				</motion.button>
			)}

			<div className="relative">
				<input
					{...register(formKey, {
						valueAsNumber: true,
						min,
						max,
					})}
					type="number"
					id={formKey}
					className="w-16 text-center shadow shadow-slate-200 dark:bg-slate-700 dark:shadow-slate-900"
				/>
				{isAngle && (
					<span className="absolute top-1 right-2 font-medium" title="Degrees">
						°
					</span>
				)}
			</div>

			{isRange && (
				<motion.button
					whileHover={{
						scale: 1.05,
					}}
					whileTap={{
						scale: 0.95,
					}}
					className="rounded-full bg-slate-100 p-1 hover:bg-slate-200 disabled:pointer-events-none disabled:opacity-50 dark:bg-slate-700 dark:hover:bg-slate-600"
					type="button"
					disabled={value >= max}
					onClick={() => {
						const value = watch(formKey) ?? 0;
						if (Number.isNaN(value))
							setValue(formKey, min, { shouldDirty: true });
						if (value + step > max) return;
						setValue(formKey, value + step, { shouldDirty: true });
					}}
				>
					<PlusIcon size={20} />
				</motion.button>
			)}
		</div>
	);
};

export default FormNumber;
